import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { Play, SkipForward, CheckCircle2, XCircle, Filter, AlertTriangle, Copy, RefreshCw } from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'What does FILTER do?',
    options: [
      'Sorts a range by a condition',
      'Returns only the rows that match your condition',
      'Deletes rows that do not match',
      'Highlights matching rows in colour',
    ],
    correctIndex: 1,
    explanation:
      'FILTER returns a new set of rows that match your condition. The original data is untouched — FILTER creates a filtered copy.',
  },
  {
    question: '=FILTER(A:C, A:A="X") returns...',
    options: [
      'Only column A where column A is "X"',
      'All columns A through C, but only rows where column A is "X"',
      'The count of rows where A is "X"',
      'TRUE or FALSE for each row',
    ],
    correctIndex: 1,
    explanation:
      'FILTER(A:C, A:A="X") returns all three columns (A, B, and C) but only for the rows where column A equals "X".',
  },
  {
    question: 'How is FILTER better than manually copying rows?',
    options: [
      'It is faster to type',
      'It uses less memory',
      'It updates automatically when data changes',
      'It looks nicer',
    ],
    correctIndex: 2,
    explanation:
      'When the source data changes — new rows added, values updated — the FILTER result updates automatically. A manually copied table is frozen in time and quickly becomes stale.',
  },
  {
    question: 'To filter by two conditions, you...',
    options: [
      'Use two separate FILTER formulas',
      'Add another condition as an additional argument',
      'Nest one FILTER inside another',
      'It is not possible to filter by two conditions',
    ],
    correctIndex: 1,
    explanation:
      'FILTER accepts multiple conditions as additional arguments: =FILTER(range, condition1, condition2). All conditions must be true for a row to be included (AND logic).',
  },
  {
    question: 'FILTER returns an error when no rows match. How do you fix it?',
    options: [
      'Add a default row to the data',
      'Use TRUE instead of the condition',
      'Wrap in IFERROR',
      'Use FILTER2 instead',
    ],
    correctIndex: 2,
    explanation:
      'When no rows satisfy the condition, FILTER returns a #N/A error. Wrapping the formula in IFERROR lets you show a fallback message like "No results" instead.',
  },
  {
    question: 'Does FILTER modify the original data?',
    options: [
      'Yes, it removes non-matching rows from the source',
      'Yes, it hides non-matching rows',
      'No — it returns a separate copy of the matching rows',
      'It depends on where you place the formula',
    ],
    correctIndex: 2,
    explanation:
      'FILTER never modifies the source data. It creates a separate output of matching rows, leaving the original data completely untouched.',
  },
  {
    question: 'When using multiple conditions in FILTER, how are they combined?',
    options: [
      'OR logic — a row needs to match any one condition',
      'AND logic — a row must match ALL conditions',
      'The first condition is applied, the rest are ignored',
      'You choose AND or OR with an extra argument',
    ],
    correctIndex: 1,
    explanation:
      'Multiple conditions in FILTER use AND logic by default. A row must satisfy every condition to be included in the result.',
  },
  {
    question: '=FILTER(A2:C100, B2:B100>500) returns...',
    options: [
      'Only column B values greater than 500',
      'All columns A through C, but only rows where column B is greater than 500',
      'The number of rows where B is greater than 500',
      'An error because you can only filter on text values',
    ],
    correctIndex: 1,
    explanation:
      'FILTER returns all columns in the range (A through C) but only rows where the condition is met. Here, only rows where column B exceeds 500 are included.',
  },
  {
    question: 'What is wrong with =FILTER(A2:C10, A2:A20="offer_123")?',
    options: [
      'Nothing — it works fine',
      'The range and condition have different row counts, which causes an error',
      'You cannot filter text values',
      'The condition must use the full column (A:A)',
    ],
    correctIndex: 1,
    explanation:
      'The data range has rows 2-10 (9 rows) but the condition range has rows 2-20 (19 rows). These must be the same size. Use A2:C10 with A2:A10, or A2:C20 with A2:A20.',
  },
  {
    question: 'You want rows where the offer is "offer_123" OR "offer_145". How do you write this?',
    options: [
      '=FILTER(A:C, A:A="offer_123", A:A="offer_145")',
      '=FILTER(A:C, (A:A="offer_123")+(A:A="offer_145"))',
      '=FILTER(A:C, A:A="offer_123" OR A:A="offer_145")',
      'You cannot use OR logic with FILTER',
    ],
    correctIndex: 1,
    explanation:
      'Adding conditions as separate arguments gives AND logic. For OR logic, use the + operator between conditions wrapped in parentheses: (condition1)+(condition2). A row is included if either condition is true.',
  },
  {
    question: 'What does =IFERROR(FILTER(A2:C100, A2:A100="offer_999"), "No results found") display if offer_999 does not exist?',
    options: [
      'An empty table',
      '#N/A',
      'No results found',
      '0',
    ],
    correctIndex: 2,
    explanation:
      'When no rows match, FILTER returns a #N/A error. IFERROR catches it and displays the fallback text "No results found" instead.',
  },
]

/* ─── Demo data ─── */
const ALL_ROWS = [
  { offer: 'offer_101', date: '2024-01-15', clicks: 340 },
  { offer: 'offer_123', date: '2024-01-15', clicks: 210 },
  { offer: 'offer_101', date: '2024-01-16', clicks: 290 },
  { offer: 'offer_123', date: '2024-01-16', clicks: 185 },
  { offer: 'offer_145', date: '2024-01-15', clicks: 410 },
  { offer: 'offer_123', date: '2024-01-17', clicks: 195 },
  { offer: 'offer_145', date: '2024-01-16', clicks: 380 },
  { offer: 'offer_101', date: '2024-01-17', clicks: 310 },
]

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Bad vs Good comparison visual ─── */
function ManualVsFilter() {
  const [showBadSteps, setShowBadSteps] = useState(false)
  const [showGoodResult, setShowGoodResult] = useState(false)

  const badSteps = [
    'Scroll through 1,000 rows...',
    'Find a row for offer_123, copy it...',
    'Keep scrolling... did I miss one?',
    'Paste into a new sheet...',
    'Repeat 20 more times...',
    'Wait, the data just updated. Start over.',
  ]

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="rounded-xl border-2 border-red-200 bg-red-50 p-5"
      >
        <div className="flex items-center gap-2 mb-3">
          <XCircle className="w-5 h-5 text-red-500" />
          <h4 className="font-bold text-red-800">The BAD Way: Manual Copy</h4>
        </div>
        <button
          onClick={() => setShowBadSteps((v) => !v)}
          className="text-sm text-red-600 underline mb-3"
        >
          {showBadSteps ? 'Hide steps' : 'See what this looks like...'}
        </button>
        <AnimatePresence>
          {showBadSteps && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-2 overflow-hidden"
            >
              {badSteps.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="flex items-start gap-2 text-sm text-red-700"
                >
                  <Copy className="w-3.5 h-3.5 shrink-0 mt-0.5 text-red-400" />
                  {step}
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
        <ul className="space-y-2 text-sm text-red-600 mt-3">
          <li className="flex items-start gap-2">
            <span className="shrink-0 mt-0.5">&#x2717;</span>
            Easy to miss rows or copy the wrong ones
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 mt-0.5">&#x2717;</span>
            Does not update when data changes
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 mt-0.5">&#x2717;</span>
            Takes 20 minutes instead of 20 seconds
          </li>
        </ul>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.15 }}
        className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-5"
      >
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-5 h-5 text-emerald-500" />
          <h4 className="font-bold text-emerald-800">The GOOD Way: FILTER</h4>
        </div>
        <button
          onClick={() => setShowGoodResult((v) => !v)}
          className="text-sm text-emerald-600 underline mb-3"
        >
          {showGoodResult ? 'Hide' : 'See what this looks like...'}
        </button>
        <AnimatePresence>
          {showGoodResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="rounded-lg bg-gray-900 px-3 py-2 mb-2">
                <code className="text-sm text-emerald-400 font-mono">=FILTER(A2:C100, A2:A100="offer_123")</code>
              </div>
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 className="w-4 h-4" />
                Done. One formula. Always current.
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <ul className="space-y-2 text-sm text-emerald-600 mt-3">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            Automatic — never misses a row
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            Updates instantly when data changes
          </li>
          <li className="flex items-start gap-2">
            <RefreshCw className="w-4 h-4 shrink-0 mt-0.5" />
            One formula replaces all that manual work
          </li>
        </ul>
      </motion.div>
    </div>
  )
}

/* ─── FILTER Animation ─── */
function FilterAnimation() {
  const [step, setStep] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const STEPS = [
    { label: 'All data', description: 'Here are all 8 rows of click data across three offers.', phase: 'all' },
    { label: 'Apply condition', description: '=FILTER(A2:C9, A2:A9="offer_123") — check each row...', phase: 'checking' },
    { label: 'Non-matching rows fade', description: 'Rows that do not match "offer_123" are excluded.', phase: 'fading' },
    { label: 'Matching rows pulled out!', description: 'The 3 matching rows are "pulled out" into a clean result.', phase: 'result' },
  ]

  useEffect(() => {
    if (!isPlaying) return
    if (step >= STEPS.length - 1) {
      setIsPlaying(false)
      return
    }
    const timer = setTimeout(() => setStep((s) => s + 1), 1800)
    return () => clearTimeout(timer)
  }, [step, isPlaying])

  const handlePlay = () => {
    setStep(0)
    setIsPlaying(true)
  }

  const currentPhase = STEPS[step].phase
  const matchingRows = ALL_ROWS.filter((r) => r.offer === 'offer_123')

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlay}
          className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
        >
          <Play className="w-4 h-4" />
          {step > 0 ? 'Replay' : 'Play'}
        </button>
        <button
          onClick={() => {
            setIsPlaying(false)
            setStep((s) => (s >= STEPS.length - 1 ? 0 : s + 1))
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <SkipForward className="w-4 h-4" />
          Step
        </button>
        <span className="text-xs text-gray-400">Step {step + 1} of {STEPS.length}</span>
      </div>

      <div className="flex gap-1">
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? 'bg-purple-500' : 'bg-gray-200'}`} />
        ))}
      </div>

      <div className="rounded-lg bg-gray-900 px-4 py-2">
        <code className="text-sm text-purple-400 font-mono">=FILTER(A2:C9, A2:A9="offer_123")</code>
      </div>

      <AnimatePresence mode="wait">
        <motion.p
          key={step}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="text-sm text-purple-700 bg-purple-50 rounded-lg px-3 py-2 border border-purple-200"
        >
          <strong>{STEPS[step].label}</strong> {STEPS[step].description}
        </motion.p>
      </AnimatePresence>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Source table */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">Source Data</p>
          <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border-b border-r border-gray-300 px-3 py-1.5 text-xs text-gray-600 font-medium">A (Offer)</th>
                  <th className="border-b border-r border-gray-300 px-3 py-1.5 text-xs text-gray-600 font-medium">B (Date)</th>
                  <th className="border-b border-gray-300 px-3 py-1.5 text-xs text-gray-600 font-medium">C (Clicks)</th>
                </tr>
              </thead>
              <tbody>
                {ALL_ROWS.map((row, i) => {
                  const isMatch = row.offer === 'offer_123'
                  const shouldFade = (currentPhase === 'fading' || currentPhase === 'result') && !isMatch
                  const shouldHighlight = (currentPhase === 'checking' || currentPhase === 'fading' || currentPhase === 'result') && isMatch
                  const shouldPull = currentPhase === 'result' && isMatch

                  return (
                    <motion.tr
                      key={i}
                      animate={{
                        opacity: shouldFade ? 0.15 : 1,
                        x: shouldFade ? -4 : shouldPull ? 8 : 0,
                        scale: shouldPull ? 1.02 : 1,
                      }}
                      transition={{ duration: 0.4, delay: shouldFade ? i * 0.05 : 0 }}
                    >
                      <td className={`border-b border-r border-gray-200 px-3 py-1.5 font-mono text-xs transition-colors duration-300 ${shouldHighlight ? 'bg-purple-100 text-purple-800 font-bold' : 'text-gray-800'}`}>
                        {isMatch && shouldHighlight && <CheckCircle2 className="w-3 h-3 inline mr-1 text-purple-600" />}
                        {row.offer}
                      </td>
                      <td className={`border-b border-r border-gray-200 px-3 py-1.5 text-xs transition-colors duration-300 ${shouldHighlight ? 'bg-purple-50 text-purple-700' : 'text-gray-700'}`}>
                        {row.date}
                      </td>
                      <td className={`border-b border-gray-200 px-3 py-1.5 text-xs transition-colors duration-300 ${shouldHighlight ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700'}`}>
                        {row.clicks}
                      </td>
                    </motion.tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Result table */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">FILTER Result</p>
          <AnimatePresence>
            {currentPhase === 'result' ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="overflow-hidden rounded-lg border-2 border-purple-300 shadow-sm"
              >
                <div className="bg-purple-600 text-white text-center py-1.5 text-xs font-bold flex items-center justify-center gap-1">
                  <Filter className="w-3.5 h-3.5" />
                  3 rows returned
                </div>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border-b border-r border-purple-200 px-3 py-1.5 text-xs text-purple-700 font-medium">Offer</th>
                      <th className="border-b border-r border-purple-200 px-3 py-1.5 text-xs text-purple-700 font-medium">Date</th>
                      <th className="border-b border-purple-200 px-3 py-1.5 text-xs text-purple-700 font-medium">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchingRows.map((row, i) => (
                      <motion.tr
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.12, type: 'spring', stiffness: 300 }}
                      >
                        <td className="border-b border-r border-purple-100 px-3 py-1.5 font-mono text-xs text-purple-800">{row.offer}</td>
                        <td className="border-b border-r border-purple-100 px-3 py-1.5 text-xs text-purple-700">{row.date}</td>
                        <td className="border-b border-purple-100 px-3 py-1.5 text-xs text-purple-700 font-semibold">{row.clicks}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            ) : (
              <motion.div
                className="flex items-center justify-center h-32 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 text-sm"
              >
                {currentPhase === 'all' ? 'Waiting for FILTER...' : 'Filtering...'}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

/* ─── Multi-condition interactive demo ─── */
function MultiConditionDemo() {
  const [activeTab, setActiveTab] = useState('one')

  const singleFiltered = ALL_ROWS.filter((r) => r.offer === 'offer_123')
  const doubleFiltered = ALL_ROWS.filter((r) => r.offer === 'offer_123' && r.date >= '2024-01-16')

  const tabs = [
    { id: 'one', label: '1 condition', formula: '=FILTER(A2:C9, A2:A9="offer_123")', rows: singleFiltered, color: 'purple' },
    { id: 'two', label: '2 conditions', formula: '=FILTER(A2:C9, A2:A9="offer_123", B2:B9>="2024-01-16")', rows: doubleFiltered, color: 'blue' },
  ]

  const active = tabs.find((t) => t.id === activeTab)

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? `bg-${tab.color}-600 text-white`
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rounded-lg bg-gray-900 px-4 py-2 overflow-x-auto">
        <code className={`text-sm font-mono text-${active.color}-400`}>{active.formula}</code>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Source with highlights */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">Source (matches highlighted)</p>
          <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border-b border-r border-gray-300 px-2 py-1.5 text-xs text-gray-600 font-medium">Offer</th>
                  <th className="border-b border-r border-gray-300 px-2 py-1.5 text-xs text-gray-600 font-medium">Date</th>
                  <th className="border-b border-gray-300 px-2 py-1.5 text-xs text-gray-600 font-medium">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {ALL_ROWS.map((row, i) => {
                  const isMatch = active.rows.some((r) => r.offer === row.offer && r.date === row.date && r.clicks === row.clicks)
                  return (
                    <tr key={i} className={isMatch ? `bg-${active.color}-50` : 'opacity-40'}>
                      <td className={`border-b border-r border-gray-200 px-2 py-1 font-mono text-[11px] ${isMatch ? `text-${active.color}-800 font-bold` : 'text-gray-600'}`}>
                        {isMatch && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                        {row.offer}
                      </td>
                      <td className={`border-b border-r border-gray-200 px-2 py-1 text-[11px] ${isMatch ? `text-${active.color}-700` : 'text-gray-500'}`}>{row.date}</td>
                      <td className={`border-b border-gray-200 px-2 py-1 text-[11px] ${isMatch ? `text-${active.color}-700 font-semibold` : 'text-gray-500'}`}>{row.clicks}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Result table */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">Result ({active.rows.length} rows)</p>
          <div className={`overflow-hidden rounded-lg border-2 border-${active.color}-300 shadow-sm`}>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className={`bg-${active.color}-50`}>
                  <th className={`border-b border-r border-${active.color}-200 px-2 py-1.5 text-xs text-${active.color}-700 font-medium`}>Offer</th>
                  <th className={`border-b border-r border-${active.color}-200 px-2 py-1.5 text-xs text-${active.color}-700 font-medium`}>Date</th>
                  <th className={`border-b border-${active.color}-200 px-2 py-1.5 text-xs text-${active.color}-700 font-medium`}>Clicks</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="wait">
                  {active.rows.map((row, i) => (
                    <motion.tr
                      key={`${activeTab}-${i}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }}
                    >
                      <td className={`border-b border-r border-${active.color}-100 px-2 py-1.5 font-mono text-[11px] text-${active.color}-800`}>{row.offer}</td>
                      <td className={`border-b border-r border-${active.color}-100 px-2 py-1.5 text-[11px] text-${active.color}-700`}>{row.date}</td>
                      <td className={`border-b border-${active.color}-100 px-2 py-1.5 text-[11px] text-${active.color}-700 font-semibold`}>{row.clicks}</td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Main Module ─── */
export default function Module7() {
  return (
    <ModuleLayout moduleId={8}>
      <div className="space-y-8">
        {/* Section 1: The Problem with Manual Tables */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">The Problem with Manual Tables</h2>
              <p className="text-lg text-gray-600">
                You have 1,000 rows of data for all offers. You only want offer_123. What do you do?
              </p>
            </div>

            <ManualVsFilter />
          </div>
        </Section>

        {/* Section 2: FILTER Explained */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">FILTER Explained</h2>
              <p className="text-lg text-gray-600">
                FILTER returns only the rows from a range where your condition is true.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-base font-mono">
                <span className="text-gray-500">=</span>
                <span className="text-purple-400">FILTER</span>
                <span className="text-gray-500">(</span>
                <span className="text-green-300">range</span>
                <span className="text-gray-500">, </span>
                <span className="text-amber-300">condition1</span>
                <span className="text-gray-500">, [</span>
                <span className="text-blue-300">condition2</span>
                <span className="text-gray-500">])</span>
              </code>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="bg-green-900/40 text-green-300 rounded px-2 py-0.5">range = the data to filter</span>
                <span className="bg-amber-900/40 text-amber-300 rounded px-2 py-0.5">condition1 = which rows to keep</span>
                <span className="bg-blue-900/40 text-blue-300 rounded px-2 py-0.5">condition2 = optional additional filter</span>
              </div>
            </div>

            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
              <p className="text-sm text-purple-800">
                <strong>Read it like English:</strong> "Filter the data where column A equals offer_123"
              </p>
              <div className="mt-2 rounded-lg bg-gray-900 px-3 py-2">
                <code className="text-sm text-purple-400 font-mono">=FILTER(A2:C100, A2:A100="offer_123")</code>
              </div>
            </div>

            <FilterAnimation />
          </div>
        </Section>

        {/* Section 3: Multiple Conditions */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Multiple Conditions</h2>
              <p className="text-lg text-gray-600">
                Need to filter by offer AND date? Just add another condition.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-sm text-purple-400 font-mono">
                =FILTER(A2:C100, A2:A100="offer_123", B2:B100&gt;="2024-01-01")
              </code>
              <p className="mt-2 text-xs text-gray-400">
                Filter where offer is "offer_123" AND date is on or after 2024-01-01
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Each additional argument acts as an AND condition. A row must pass <strong>all</strong> conditions to be included.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="rounded-lg border-2 border-purple-200 bg-purple-50 px-4 py-2"
                >
                  <p className="text-xs text-purple-600 font-medium">Stage 1</p>
                  <p className="text-sm font-semibold text-purple-800">Offer = offer_123?</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-purple-400 font-bold"
                >
                  AND
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="rounded-lg border-2 border-blue-200 bg-blue-50 px-4 py-2"
                >
                  <p className="text-xs text-blue-600 font-medium">Stage 2</p>
                  <p className="text-sm font-semibold text-blue-800">Date &gt;= 2024-01-16?</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="text-emerald-400 font-bold"
                >
                  =
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="rounded-lg border-2 border-emerald-200 bg-emerald-50 px-4 py-2"
                >
                  <p className="text-xs text-emerald-600 font-medium">Result</p>
                  <p className="text-sm font-semibold text-emerald-800">Both conditions met</p>
                </motion.div>
              </div>
            </div>

            <MultiConditionDemo />

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800">
                    <strong>Tip:</strong> If no rows match all conditions, FILTER returns a #N/A error. Wrap in IFERROR to handle it gracefully:
                  </p>
                  <div className="mt-2 rounded-lg bg-gray-900 px-3 py-2">
                    <code className="text-xs text-amber-400 font-mono">=IFERROR(FILTER(A2:C100, A2:A100="offer_999"), "No results found")</code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Section 4: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={8} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
